<template>
    <div
        class="filter-location element can-grow has-flyout"
        :class="[{ visible: locations.length > 0 }, { placeholder }, classes]"
    >
        <div
            class="filter-trigger location-filter-trigger"
            :class="{
                active: locationIsActive,
                inputting: hasLocationSearchTerm,
                populated: hasLocationSearchTerm,
                focused: locationSearchFocused,
                'uses-placeholder': placeholderText,
            }"
        >
            <div
                class="filter-location-group"
                :class="{ loading: loading }"
                @click.prevent="$refs.locationInput.focus"
            >
                <label
                    v-if="placeholder"
                    :for="`filter-location-region_${this._uid}`"
                    class="label"
                >
                    Location
                    <svg-icon id="loading" class="spin"></svg-icon>
                </label>

                <div class="current-value-wrap">
                    <div class="current-value-group">
                        <span class="placeholder" v-if="placeholderText">{{
                            placeholderText
                        }}</span>
                        <span
                            class="current-value"
                            :title="selected.name || ''"
                        >
                            {{
                                selected.name || (placeholder ? "Location" : "")
                            }}
                        </span>
                        <button
                            type="button"
                            class="button tiny rounded grey clear-all"
                            :class="{ hidden: !selected.type }"
                            @click="resetLocationSearch"
                        >
                            <svg-icon id="cross"></svg-icon>
                        </button>
                        <div
                            class="filter-radius"
                            :class="{
                                hidden: !requiresLocationRadius,
                                disabled: !selected.type,
                            }"
                        >
                            <select-wrapper
                                :id="`filter-location-radius_${this._uid}`"
                                :options="radiusOptions"
                                v-model="selected.radius"
                                :append-to-body="false"
                            ></select-wrapper>
                        </div>
                    </div>
                    <input
                        :id="`filter-location-region_${this._uid}`"
                        class="filter-location-region"
                        ref="locationInput"
                        :title="selected.name || ''"
                        type="text"
                        v-model="input"
                        @focus="locationSearchFocused = true"
                        @blur="locationSearchFocused = false"
                        @change="$emit('change', $event)"
                    />

                    <svg-icon id="search" v-if="!selected.type"></svg-icon>
                </div>
            </div>
        </div>

        <div
            class="flyout-menu region-results pos-left"
            :class="{ hidden: locations.length === 0 }"
        >
            <vue-simplebar class="inner">
                <ul id="location-regions" class="listing location-options">
                    <li
                        v-for="result in locations"
                        @click="setLocation(result)"
                    >
                        <svg-icon :id="locationIcon(result)"></svg-icon>
                        {{ result.label }}
                    </li>
                </ul>
            </vue-simplebar>
        </div>
    </div>
</template>

<script>
import debounce from "lodash/debounce";

export default {
    props: {
        placeholder: {
            type: Boolean,
            required: false,
            default: true,
        },
        placeholderText: {
            type: String,
            default: null,
        },
        externalSelection: {
            required: false,
        },
        initialValue: {
            required: false,
        },
        classes: {
            type: String,
            required: false,
        },
    },
    created() {
        if (this.initialValue && typeof this.initialValue === "object") {
            this.selected = this.initialValue;
        }
    },
    data() {
        return {
            loading: false,
            input: "",
            locations: [],
            locationSearchFocused: false,
            selected: {
                location: null,
                name: null,
                type: null,
                radius: "25 km",
            },
        };
    },
    methods: {
        delegatedHideDropdown(e) {
            if (!e.target.closest(".region-results")) {
                this.locations = [];
            }
        },
        fetchLocations() {
            if (!this.input || this.input === "") {
                return;
            }
            this.loading = true;
            axios
                .get("/api/locations", {
                    params: {
                        query: this.input,
                    },
                })
                .then((response) => (this.locations = response.data || []))
                .catch(() => {})
                .then(() => (this.loading = false));
        },
        setLocation(result) {
            this.selected.location = result.type + ":" + result.id;
            this.selected.name = result.label;
            this.selected.type = result.type;
            this.selected.radius = this.selected.radius || 25;
            this.input = "";
            this.locations = [];
            this.$emit("input", this.selected);
        },
        locationIcon(result) {
            switch (result.type) {
                case "lga":
                    return "tentMoon";
                case "region":
                    return "toggleMap";
            }

            return "mapPin";
        },
        resetLocationSearch() {
            this.selected.location = null;
            this.selected.name = null;
            this.selected.type = null;
            this.selected.radius = 25;
            this.$emit("clear");
        },
    },
    computed: {
        locationIsActive() {
            return !!this.selected.location;
        },
        hasLocationSearchTerm() {
            return this.input.length > 0;
        },
        radiusOptions() {
            return [
                { id: 0, label: "+0 km" },
                { id: 2, label: "+2 km" },
                { id: 5, label: "+5 km" },
                { id: 10, label: "+10 km" },
                { id: 15, label: "+15 km" },
                { id: 25, label: "+25 km" },
                { id: 50, label: "+50 km" },
                { id: 100, label: "+100 km" },
            ];
        },
        requiresLocationRadius() {
            return (
                this.selected.type &&
                ["place", "colloquial"].includes(this.selected.type)
            );
        },
    },
    watch: {
        input: debounce(function () {
            if (
                (this.locationIsActive && this.input === "") ||
                this.input.length < 3
            ) {
                return;
            }
            this.fetchLocations();
        }, 250),

        externalSelection: {
            handler() {
                console.info(this.externalSelection);
                this.setLocation(this.externalSelection);
            },
            deep: true,
        },

        "selected.radius"() {
            this.$emit("input", this.selected);
        },
    },
    mounted() {
        window.addEventListener("click", this.delegatedHideDropdown);
    },
    beforeDestroy() {
        window.removeEventListener("click", this.delegatedHideDropdown);
    },
};
</script>
